import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import ActivityFeed from '../ActivityFeed.jsx';
import { WORKSPACE_FUNDS } from '../../context/WorkspaceContext.jsx';

export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'];
export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done'];
export const blankTask = {
  title: '', description: '', project_id: '', fund_client_id: '', assignee_id: '',
  priority: 'medium', status: 'todo', start_date: '', due_date: '',
};

/**
 * Create/edit a task. Reused by the Tasks board, Project detail and fund-client
 * pages. Pass `lockProject` to pin the task to its project. Checklist items are
 * persisted immediately (they need a saved task id), shown only when editing.
 */
export default function TaskForm({
  initial,
  projects = [],
  fundClients = [],
  users = [],
  workspace,
  canDelete = true,
  lockProject = false,
  lockFundClient = false,
  onClose,
  onSaved,
}) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState({ ...blankTask, ...initial });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isFunds = workspace === WORKSPACE_FUNDS
    || !!(initial.fund_client_id)
    || (lockFundClient && initial.fund_client_id);

  const itemKey = (it) => it.id ?? it.tempId;

  useEffect(() => {
    if (!isNew) api.get(`/tasks/${initial.id}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [initial.id, isNew]);

  async function save() {
    setBusy(true); setErrors({});
    try {
      const payload = {
        ...form,
        project_id: isFunds ? null : (form.project_id || null),
        fund_client_id: isFunds ? (form.fund_client_id || null) : null,
      };
      if (isNew) {
        const res = await api.post('/tasks', payload);
        const newId = res?.id;
        if (newId) {
          for (const it of items) {
            await api.post(`/tasks/${newId}/items`, { title: it.title });
          }
        }
      } else {
        await api.put(`/tasks/${initial.id}`, payload);
      }
      toast(isNew ? t('Task created') : t('Task saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete task'), message: t('Delete this task?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/tasks/${initial.id}`);
    toast(t('Task deleted'));
    onSaved();
  }

  async function addItem() {
    const title = newItem.trim();
    if (!title) return;
    if (isNew) {
      setItems((xs) => [...xs, { tempId: `t${Date.now()}${Math.random()}`, title, is_done: 0 }]);
      setNewItem('');
      return;
    }
    await api.post(`/tasks/${initial.id}/items`, { title });
    setNewItem('');
    const r = await api.get(`/tasks/${initial.id}`);
    setItems(r.data.items || []);
  }
  async function toggleItem(it) {
    setItems((xs) => xs.map((x) => (itemKey(x) === itemKey(it) ? { ...x, is_done: it.is_done ? 0 : 1 } : x)));
    if (it.id) await api.patch(`/task-items/${it.id}`, { is_done: it.is_done ? 0 : 1 });
  }
  async function deleteItem(it) {
    setItems((xs) => xs.filter((x) => itemKey(x) !== itemKey(it)));
    if (it.id) await api.del(`/task-items/${it.id}`);
  }

  const doneCount = items.filter((i) => i.is_done).length;

  return (
    <Modal title={isNew ? t('New task') : t('Edit task')} onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify({ ...blankTask, ...initial })}
      footer={
        <div className="modal-actions">
          {!isNew && canDelete && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Title')} error={errors.title}><input value={form.title} onChange={set('title')} /></Field>
      <Field label={t('Description')}><textarea rows={2} value={form.description || ''} onChange={set('description')} /></Field>
      <div className="grid-2">
        {isFunds ? (
          <Field label={t('Fund client')}>
            <select value={form.fund_client_id || ''} onChange={set('fund_client_id')} disabled={lockFundClient || lockProject}>
              <option value="">—</option>
              {fundClients.map((fc) => (
                <option key={fc.id} value={fc.id}>
                  {fc.client_name}{fc.fund_name ? ` · ${fc.fund_name}` : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label={t('Project')}>
            <select value={form.project_id || ''} onChange={set('project_id')} disabled={lockProject}>
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        )}
        <Field label={t('Assignee')}>
          <select value={form.assignee_id || ''} onChange={set('assignee_id')}>
            <option value="">—</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid-2">
        <Field label={t('Priority')}><select value={form.priority} onChange={set('priority')}>{TASK_PRIORITIES.map((p) => <option key={p} value={p}>{label(p)}</option>)}</select></Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{TASK_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
      </div>
      <div className="grid-2">
        <Field label={t('Start date')} error={errors.start_date}><input type="date" value={form.start_date || ''} onChange={set('start_date')} /></Field>
        <Field label={t('Due date')} error={errors.due_date}><input type="date" value={form.due_date || ''} onChange={set('due_date')} /></Field>
      </div>

      <div className="checklist">
        <div className="field-label">{t('Checklist')} {items.length > 0 && <span className="muted sm">({doneCount}/{items.length})</span>}</div>
        <ul className="checklist-list">
          {items.map((it) => (
            <li key={itemKey(it)}>
              <label>
                <input type="checkbox" checked={!!it.is_done} onChange={() => toggleItem(it)} />
                <span className={it.is_done ? 'done' : ''}>{it.title}</span>
              </label>
              <button className="icon-btn xs" title={t('Remove')} onClick={() => deleteItem(it)} aria-label={t('Remove')}>×</button>
            </li>
          ))}
        </ul>
        <div className="checklist-add">
          <input placeholder={t('Add a checklist item…')} value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} />
          <button className="btn ghost sm" type="button" onClick={addItem}>{t('Add')}</button>
        </div>
        {isNew && <p className="muted sm" style={{ marginTop: 6 }}>{t('Items are saved together with the task.')}</p>}
      </div>

      {!isNew && (
        <ActivityFeed
          entityType="task"
          entityId={initial.id}
          title={t('Activity')}
          limit={12}
          compact
        />
      )}
    </Modal>
  );
}
